import {Box, Typography} from '@mui/material';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {useInsertSecret} from '@wandb/weave/common/hooks/useSecrets';
import {Alert} from '@wandb/weave/components/Alert';
import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox';
import {TextArea} from '@wandb/weave/components/Form/TextArea';
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useState} from 'react';

import {sharedTypographyStyle} from './AddProviderDrawerFormComponents';

const validateSecretName = (name: string) => {
  // Any alphanumeric+underscore string starting with an alphabet character or underscore
  const re = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
  return re.test(String(name));
};

export const CreateSecretSubview: React.FC<{
  entityName: string;
  onClose: () => void;
  onSecretCreated?: (secretName: string) => void;
}> = ({entityName, onClose, onSecretCreated}) => {
  const [secretName, setSecretName] = useState('');
  const [secretValue, setSecretValue] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [isSecretNameModified, setIsSecretNameModified] = useState(false);

  const insertSecret = useInsertSecret();

  const onClickAddSecret = async () => {
    try {
      await insertSecret({
        variables: {entityName, secretName, secretValue},
      });
      toast(`Added secret ${secretName}`, {
        type: 'success',
      });
      onSecretCreated?.(secretName);
      onClose();
    } catch (err: any) {
      const errMsg = err?.message || 'Unknown error';
      console.error(errMsg);
      toast(`Problem adding secret ${secretName}: ${errMsg}`, {
        type: 'error',
      });
    }
  };

  const inputError = isSecretNameModified && !validateSecretName(secretName);
  const canAddSecret =
    !inputError && secretValue.length > 0 && secretName.length > 0;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          overflow: 'auto',
        }}>
        <Alert severity="info">
          This secret will be created for <strong>{entityName}</strong>, owner
          of the destination project.
          <br />
          Team secrets can be managed in settings by team admins.
        </Alert>

        <Box>
          <Typography sx={{...sharedTypographyStyle, mb: 1, fontWeight: '600'}}>
            Secret name
          </Typography>
          <TextField
            placeholder="Ex. MY_API_APP_KEY"
            value={secretName}
            onChange={value => {
              setSecretName(value);
              setIsSecretNameModified(true);
            }}
            errorState={inputError}
          />
          {inputError && (
            <Typography
              variant="caption"
              color="error"
              sx={{...sharedTypographyStyle, mt: 0.5, display: 'block'}}>
              Secret names can only contain alphanumeric characters ([a-z],
              [A-Z], [0-9]) or underscores (_) and start with a letter ([a-z],
              [A-Z]) or underscores (_).
            </Typography>
          )}
        </Box>

        <Box>
          <Typography sx={{...sharedTypographyStyle, mb: 1, fontWeight: '600'}}>
            Secret
          </Typography>
          <TextArea
            style={{
              height: '80px',
              minHeight: '80px',
              // @ts-ignore: WebkitTextSecurity is a non-standard CSS property
              WebkitTextSecurity: showSecret ? 'none' : 'disc',
            }}
            value={secretValue}
            placeholder="Paste your key or token here"
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setSecretValue(e.target.value)
            }
          />
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 1}}>
            <Checkbox
              id="reveal-secret-checkbox"
              checked={showSecret}
              onCheckedChange={() => setShowSecret(!showSecret)}
              size="small"
            />
            <Typography
              component="label"
              htmlFor="reveal-secret-checkbox"
              sx={{...sharedTypographyStyle, cursor: 'pointer'}}>
              Reveal secret
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          py: 2,
          px: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          width: '100%',
          display: 'flex',
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
        }}>
        <Box sx={{display: 'flex', gap: 2, width: '100%', mx: 2}}>
          <Button
            onClick={onClose}
            variant="secondary"
            style={{flex: 1}}
            twWrapperStyles={{flex: 1}}>
            Cancel
          </Button>
          <Button
            onClick={onClickAddSecret}
            disabled={!canAddSecret}
            variant="primary"
            style={{flex: 1}}
            twWrapperStyles={{flex: 1}}>
            Add secret
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
